import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import i18next from 'i18next';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  closeGenericModal,
  openGenericModal,
  openSmallModal,
  refreshGenericModal,
} from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import { Button, ListView } from '@commons/utils/styledLibraryComponents';
import { formatNumber } from '@commons/DisplayNumber';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTheme } from '@commons/utils/theme';
import { handleOpenedImportFileModal } from '@commons/Modals/ImportModal/utils/config';
import { IMPORT_STATE, IMPORT_TYPE, SWITCH_STEP } from '@commons/Modals/ImportModal/utils/constant';
import { isUserManager, userHasRoleInpulse } from '@commons/utils/roles';
import { STANDARD_LISTVIEW_PADDING } from '@commons/constants/listViewProps';
import EmptyState from '@commons/EmptyState';
import GeneralEmptyStateListView from '@commons/GeneralEmptyStateListView';
import mixpanelUtils, { ENUM_EVENT_SOURCES, ENUM_EVENTS } from '@commons/utils/mixpanel';

import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

import { IngredientContext } from '@context/IngredientContext';

import { getAuthorizedActions, getPrevHorizon } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';

import { ingredient as ingredientService } from '@services/ingredient';
import clientService from '@services/client';

import { downloadFile, formatXlsFileToJson } from '@backoffice/utils';

import {
  CHOICES_DROPDOWN_ACTIVE,
  STRATEGIC_INGREDIENT_DROPDOWN_ITEMS,
} from '@admin/utils/DropdownItems';
import DeepsightFiltersButton from '@admin/components/FilterButton';

import { formatEntityBatchCreationFile } from './utils/formatEntityBatchCreationFile';
import { formatEntityBatchUpdateFile } from './utils/formatEntityBatchUpdateFile';
import { getActions, getEmptyStateActions, getRowActions } from './utils/getActions';
import {
  getActivationWarningModal,
  getDeactivationWarningModal,
} from './utils/modalConfigurations';
import { getColumns, getColumnsFilterList } from './utils/getColumns';
import { ListViewContainer } from './styledComponents';
import IngredientExportModal from './components/IngredientExportModal';

import { CATEGORY_TYPES_OBJECT } from '@commons/constants/categoryTypes';
import { IMPORT_MODAL_STEPS } from '@commons/Modals/MultiStepModal';
import paginationUtils from '@commons/utils/listViewPagination';

import { ENUM_QUERY_PARAMS, useListViewQueryParams } from '@hooks/useListViewQueryParams';
import { getListViewPersistentFilters, PersistentFilterTypes } from '@context/utils';
import { useCategoriesBatchUpdateModal } from '@hooks/useCategoriesBatchUpdateModal';

import { getImportErrorFileConfig } from './utils/importErrorFile';
import { getUpdateIngredientModalConfig } from './utils/updateModaleConfig';

const ENUM_CUSTOM_FILTERS = {
  IS_STRATEGIC: 'is-strategic',
  ACTIVE_INACTIVE_SUPPLIER_PRODUCTS: 'active-inactive-supplier-products',
};

moment.locale('fr');

export const Ingredients = (props) => {
  const {
    client: { clientId, storeName },
    user,
    match: { path },
    history,
    currency,
    openModalExportInfo,
    modal,
    openGenericModal,
    refreshGenericModal,
    closeGenericModal,
    pageLoaded,
    pageLoading,
    showErrorMessage,
    showSuccessMessage,
    authorizedActions,
    prevHorizon,
  } = props;

  const contextHandler = useContext(IngredientContext) || {};

  const {
    selectedStatus,
    setSelectedStatus,
    selectedStrategicFilters,
    setSelectedStrategicFilters,
    customParams,
    setCustomParams,
    advancedFilters,
    setAdvancedFilters,
    search,
  } = contextHandler;

  const storeNamePlural = getClientStoreNameTranslation(storeName, true).toLowerCase();
  const storeNameSingular = getClientStoreNameTranslation(storeName);

  const persistentFilters = getListViewPersistentFilters(contextHandler);

  const [userLanguageCode] = useState(get(user, 'lnkLanguageAccountrel.code', 'fr'));

  const activeFilterValue = useMemo(
    () => get(selectedStatus, 'filterValue', false),
    [selectedStatus],
  );

  // Data states
  const [ingredients, setIngredients] = useState([]);
  const [totalIngredients, setTotalIngredients] = useState(0);

  // Categories
  const [ingredientsCategories, setIngredientsCategories] = useState([]);

  // List View
  const [columns] = useState(getColumns());
  const [actions, setActions] = useState([]);
  const [rowActions, setRowActions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [listViewQueryParams, setListViewQueryParams] = useListViewQueryParams(false);
  const [paginationProps, setPaginationProps] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialFetch, setIsInitialFetch] = useState(true);

  // Filter states
  const [filters, setFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(false);
  const [columnsFilterList, setColumnsFilterList] = useState([]);

  const [emptyState, setEmptyState] = useState(false);
  const [emptyStateActions, setEmptyStateActions] = useState([]);

  // Import modal states
  const [currentStep, setCurrentStep] = useState(IMPORT_STATE.SELECT_FILE);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorFile, setErrorFile] = useState(null);
  const [batchEntitiesToCreate, setBatchEntitiesToCreate] = useState(null);

  const listViewRef = useRef();
  const hasInitializedFromUrl = useRef(false);

  /***************/
  /*  Effects */
  /***************/

  // Retrieve categories
  useEffect(() => {
    (async () => {
      try {
        const { categories } = await ingredientService.getIngredientsCategories();
        const formattedCategories = categories.map((category) => ({
          ...category,
          name: category.name || i18next.t('GENERAL.NONE_VALUE'),
        }));

        setIngredientsCategories(formattedCategories);
      } catch {
        showErrorMessage(i18next.t('ADMIN.PRODUCTS.CATEGORIES_FETCH_ERROR'));
      }
    })();
  }, []);

  // Build filters
  useEffect(() => {
    setColumnsFilterList(getColumnsFilterList(columns, ingredientsCategories));
  }, [ingredientsCategories, columns]);

  useEffect(() => {
    updateListActions();
    updateRowActionsList();
    updateEmptyStateActions();
  }, [selectedItems, ingredients]);

  useEffect(() => {
    if (!selectedFile) {
      return;
    }

    handleOpenedImportFileModal(
      {
        currentStep: currentStep,
        importType: IMPORT_TYPE.ENTITIES,
        handleSwitchStep: handleSwitchStep,
        handleFileChange: handleFileChange,
        selectedFile: selectedFile,
        exportErrorData: errorFile,
        cleanImportModal: cleanUpModalImport,
      },
      openGenericModal,
      refreshGenericModal,
      get(modal, 'GenericModalBool', false),
    );
  }, [selectedFile, currentStep]);

  // Update data to be equal to the context
  useEffect(() => {
    if (isEmpty(contextHandler)) {
      return;
    }

    // Skip on initial mount if URL already has query params (from refresh or direct URL)
    // But allow context to sync to URL when coming back from detail page (no URL params)
    if (!hasInitializedFromUrl.current && listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY]) {
      hasInitializedFromUrl.current = true;
      return;
    }

    const listViewQueryParamsToUpdate = persistentFilters.filter(
      ({ filterType }) => filterType === PersistentFilterTypes.LIST_VIEW,
    );

    listViewQueryParamsToUpdate.forEach(({ contextParam, queryParam }) => {
      if (contextParam && contextParam.length && listViewQueryParams[queryParam] !== contextParam) {
        setListViewQueryParams[queryParam](contextParam);
      }
    });
  }, [
    /*
        We watch every variable instead of only contextHandler because it is refreshed at every change and we don't
        want to enter in this useEffect on every change, only when a state is changed
      */
    !!contextHandler &&
      (contextHandler.search ||
        contextHandler.orderBy ||
        contextHandler.orderType ||
        contextHandler.maxPerPage ||
        contextHandler.currentPage),
  ]);

  useEffect(() => {
    if (
      !listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE] &&
      !listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY] &&
      !listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE] &&
      !listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE]
    ) {
      return;
    }

    // Don't fetch if filters are being applied - the applyFilters useEffect will handle it
    if (applyFilters) {
      return;
    }

    const contextFiltersToUpdate = persistentFilters.filter(
      ({ filterType }) => filterType === PersistentFilterTypes.LIST_VIEW,
    );
    contextFiltersToUpdate.forEach(({ contextParam, updateFunction, queryParam }) => {
      if (
        (!!listViewQueryParams[queryParam] || listViewQueryParams[queryParam] === '') &&
        listViewQueryParams[queryParam] !== contextParam
      ) {
        updateFunction(listViewQueryParams[queryParam]);
      }
    });

    getIngredients();
  }, [
    listViewQueryParams[ENUM_QUERY_PARAMS.SEARCH],
    listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_BY],
    listViewQueryParams[ENUM_QUERY_PARAMS.ORDER_TYPE],
    listViewQueryParams[ENUM_QUERY_PARAMS.MAX_PER_PAGE],
    listViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE],
    customParams,
    activeFilterValue,
  ]);

  // Set pagination props like SupplierProducts
  useEffect(() => {
    setPaginationProps(
      paginationUtils.getPropsListView({
        listViewRef,
        listViewQueryParams,
        setListViewQueryParams,
        count: totalIngredients,
        onQueryParamsChange: () => true, // not used but necessary to adapt select MaxPage ListView
      }),
    );
  }, [totalIngredients]);

  const goToIngredientDetailsPage = (id) => {
    history.push(`/admin/products/ingredients/${id}/details`);
  };

  // Apply filters
  useEffect(() => {
    if (!applyFilters || !listViewRef || !listViewRef.current) {
      return;
    }

    if ((!advancedFilters || !advancedFilters.length) && applyFilters) {
      listViewRef.current.resetPagination();

      // Trigger refresh of products when filters are reset
      setCustomParams({});
      setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](1);

      // Reset applyFilters flag
      setApplyFilters(false);

      return;
    }

    const params = advancedFilters.reduce(
      (result, { getQueryParamOperation, propertyKey, value }) => {
        result[propertyKey] = getQueryParamOperation(propertyKey, value);

        return result;
      },
      {},
    );

    // Trigger refresh of products when filters are applied
    setCustomParams(params);
    setListViewQueryParams[ENUM_QUERY_PARAMS.CURRENT_PAGE](1);

    listViewRef.current.resetPagination();

    // Reset applyFilters flag
    setApplyFilters(false);
  }, [advancedFilters, applyFilters]);

  const preprocessIngredients = (ingredients) => {
    const formattedIngredients = ingredients.map((ingredient) => {
      let costUnit = '';

      if (ingredient.unit === 'unit') {
        ingredient.unit = i18next.t('GENERAL.UNIT');
        costUnit = i18next.t('GENERAL.UNIT');
      } else if (ingredient.unit === 'g') {
        costUnit = 'kg';
      } else if (ingredient.unit === 'mL') {
        costUnit = 'L';
      }
      ingredient.costStr = ingredient.cost
        ? `${formatNumber(ingredient.cost, currency.numberDecimals)} ${
            currency.alphabeticCode
          }/ ${costUnit}`
        : '-';

      ingredient.category = ingredient.category || i18next.t('GENERAL.NONE_VALUE');

      return ingredient;
    });

    return formattedIngredients;
  };

  const getIngredients = async () => {
    setIsLoading(true);

    try {
      const columnPropertyKeys = columns.map((column) => column.propertyKey);

      const { search, skip, limit, orderBy, orderType } = paginationUtils.getPayloadPagination({
        columnPropertyKeys,
        listViewQueryParams,
      });

      // Convert skip back to page for the API (API expects page starting from 1)
      const page = Math.floor(skip / limit) + 1;

      const { data, count } = await ingredientService.getPaginatedIngredientsOfClient(
        {
          orderBy,
          orderType,
          page,
          limit,
          search: search || '',
        },
        {
          active: { eq: activeFilterValue },
          isStrategic: { inArray: selectedStrategicFilters.map((filter) => filter.itemValue) },
          categoryId: customParams.categoryId,
          unit: customParams.unit,
          cost: customParams.cost,
          nbParentMappingsCount: customParams.parentCounts,
          nbSupplierProductsCount: customParams.supplierProductMappingCount,
        },
      );

      if (!data || !data.length) {
        setIngredients([]);
        setTotalIngredients(0);

        if (isInitialFetch) {
          const hasActiveSearchOrFilters = !!search || Object.keys(customParams).length > 0;

          if (!hasActiveSearchOrFilters) {
            // Only show the GeneralEmptyStateListView if there is no active search or filters
            setEmptyState(true);
          }
        }

        return;
      }

      const formattedIngredients = preprocessIngredients(data);

      setIngredients(formattedIngredients);
      setTotalIngredients(count);
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.LOAD_ERROR'));

      setIngredients([]);
      setTotalIngredients(0);
    } finally {
      setIsLoading(false);
      setIsInitialFetch(false);
    }
  };

  const deleteIngredientByIdsWithLinkedData = async (ingredients) => {
    if (!ingredients.length) {
      return;
    }

    const ingredientIds = ingredients.map(({ id }) => id);
    const isDeletingMultipleIngredients = ingredientIds.length > 1;

    pageLoading();

    try {
      await ingredientService.deleteIngredientByIdsWithLinkedData(ingredientIds, clientId);

      await getIngredients();

      showSuccessMessage(
        i18next.t(
          isDeletingMultipleIngredients
            ? 'ADMIN.INGREDIENTS.DELETE_MULTIPLE_SUCCESS'
            : 'ADMIN.INGREDIENTS.DELETE_SINGLE_SUCCESS',
        ),
      );

      setSelectedItems([]);
    } catch {
      showErrorMessage(
        i18next.t(
          isDeletingMultipleIngredients
            ? 'ADMIN.INGREDIENTS.DELETE_MULTIPLE_ERROR'
            : 'ADMIN.INGREDIENTS.DELETE_SINGLE_ERROR',
        ),
      );
    } finally {
      pageLoaded();
    }
  };

  const handleExport = (ingredients) => {
    triggerExport(ingredients);
  };

  const updateIsStrategicOfIngredientsByBatch = async (
    ingredients = [],
    updatedIsStrategicValue,
  ) => {
    pageLoading();

    const isUniqueSelection = ingredients.length === 1;

    const payload = [
      {
        field: 'isStrategic',
        value: updatedIsStrategicValue,
        ids: ingredients.map(({ id }) => id),
      },
    ];

    try {
      await clientService.updateIngredientPropertyByBatch(clientId, payload);

      await getIngredients();

      showSuccessMessage(
        !!updatedIsStrategicValue
          ? i18next.t(
              isUniqueSelection
                ? 'GENERAL.LIST_ACTION_DEFINE_AS_STRATEGIC_SINGLE_SUCCESS'
                : 'GENERAL.LIST_ACTION_DEFINE_AS_STRATEGIC_MULTIPLE_SUCCESS',
            )
          : i18next.t(
              isUniqueSelection
                ? 'GENERAL.LIST_ACTION_DEFINE_AS_NOT_STRATEGIC_SINGLE_SUCCESS'
                : 'GENERAL.LIST_ACTION_DEFINE_AS_NOT_STRATEGIC_MULTIPLE_SUCCESS',
            ),
      );
    } catch {
      showErrorMessage(
        !!updatedIsStrategicValue
          ? i18next.t(
              isUniqueSelection
                ? 'GENERAL.LIST_ACTION_DEFINE_AS_STRATEGIC_SINGLE_FAILURE'
                : 'GENERAL.LIST_ACTION_DEFINE_AS_STRATEGIC_MULTIPLE_FAILURE',
            )
          : i18next.t(
              isUniqueSelection
                ? 'GENERAL.LIST_ACTION_DEFINE_AS_NOT_STRATEGIC_SINGLE_FAILURE'
                : 'GENERAL.LIST_ACTION_DEFINE_AS_NOT_STRATEGIC_MULTIPLE_FAILURE',
            ),
      );
    } finally {
      pageLoaded();
    }
  };

  const handleIngredientsDeactivation = (ingredientsToDeactivate) => {
    const params = getDeactivationWarningModal(ingredientsToDeactivate, disableIngredients);

    openGenericModal(params);
  };

  const disableIngredients = async (ingredients) => {
    const ingredientIds = ingredients.map(({ id }) => id);

    pageLoading();

    try {
      await ingredientService.disableIngredients(ingredientIds);

      await getIngredients();

      showSuccessMessage(
        ingredientIds.length === 1
          ? i18next.t('ADMIN.INGREDIENTS.SINGLE_DISABLE_SUCCESS')
          : i18next.t('ADMIN.INGREDIENTS.MULTIPLE_DISABLE_SUCCESS'),
      );
    } catch {
      showErrorMessage(
        ingredientIds.length === 1
          ? i18next.t('ADMIN.INGREDIENTS.SINGLE_DISABLE_ERROR')
          : i18next.t('ADMIN.INGREDIENTS.MULTIPLE_DISABLE_ERROR'),
      );
    } finally {
      pageLoaded();
    }
  };

  const handleIngredientActivation = (ingredientToActivate) => {
    const params = getActivationWarningModal({
      ingredientToActivate,
      activateIngredients,
    });

    openGenericModal(params);
  };

  const activateIngredients = async (ingredient) => {
    pageLoading();

    try {
      await ingredientService.enableIngredientsByIds([ingredient.id]);

      await getIngredients();

      showSuccessMessage(i18next.t('ADMIN.INGREDIENTS.SINGLE_ACTIVATION_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.SINGLE_ACTIVATION_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const getEntityBatchCreationTemplate = async () => {
    pageLoading();

    try {
      const template = await clientService.getEntityBatchCreationTemplate();

      downloadFile(template, i18next.t('ADMIN.INGREDIENTS.DOWNLOAD_TEMPLATE_FOR_IMPORT_FILENAME'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.TOASTER_IMPORT_BATCH_FAILED'));
    } finally {
      pageLoaded();
    }
  };

  const getIngredientsBatchUpdateTemplate = async () => {
    pageLoading();

    try {
      const template = await ingredientService.getIngredientsBatchUpdateTemplate();

      downloadFile(
        template,
        i18next.t('ADMIN.INGREDIENTS.DOWNLOAD_TEMPLATE_FOR_UPDATE_IMPORT_FILENAME'),
      );
    } catch {
      showErrorMessage(i18next.t('GENERAL.FILE_DOWNLOAD_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const cleanUpModalImport = () => {
    setSelectedFile(null);
    setErrorFile(null);
    setCurrentStep(IMPORT_STATE.SELECT_FILE);
    setBatchEntitiesToCreate(null);
    closeGenericModal();
  };

  const getEntityBatchFileValidation = async () => {
    pageLoading();

    try {
      const entities = await formatXlsFileToJson(selectedFile);

      const formattedEntities = formatEntityBatchCreationFile(entities);

      setBatchEntitiesToCreate(formattedEntities);

      await clientService.postEntityBatchCreateValidation(clientId, formattedEntities);

      setCurrentStep(IMPORT_STATE.VALIDATED_FILE);
    } catch (error) {
      if (error.statusCode === 422) {
        setErrorFile(error.data);
        setCurrentStep(IMPORT_STATE.ERROR_FILE);
      }
    } finally {
      pageLoaded();
    }
  };

  const postBatchEntitiesToCreate = async () => {
    pageLoading();

    try {
      await clientService.postEntityBatchCreation(clientId, batchEntitiesToCreate);

      showSuccessMessage(i18next.t('GENERAL.IMPORT_FILE_BATCH_CREATION_TOASTER_SUCCESS'));
      cleanUpModalImport();

      await getIngredients();
    } catch {
      showErrorMessage(i18next.t('GENERAL.IMPORT_FILE_BATCH_CREATION_TOASTER_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleFileValidation = async () => {
    await getEntityBatchFileValidation();
  };

  const handleSwitchStep = async (changeStep) => {
    if (changeStep === SWITCH_STEP.PREVIOUS) {
      cleanUpModalImport();
      return;
    }

    if (currentStep === IMPORT_STATE.SELECT_FILE) {
      await handleFileValidation();
    }

    if (currentStep === IMPORT_STATE.ERROR_FILE) {
      cleanUpModalImport();
    }

    if (currentStep === IMPORT_STATE.VALIDATED_FILE) {
      await postBatchEntitiesToCreate();
    }
  };

  const handleFileChange = (file) => {
    setSelectedFile(file);

    openImportEntitiesModal(file);
  };

  const openImportEntitiesModal = (file) => {
    const ingredientCreationFile =
      (!!file || file == null) && currentStep === IMPORT_STATE.SELECT_FILE ? file : selectedFile;

    handleOpenedImportFileModal(
      {
        currentStep: currentStep,
        importType: IMPORT_TYPE.ENTITIES,
        handleSwitchStep: handleSwitchStep,
        handleFileChange: handleFileChange,
        selectedFile: ingredientCreationFile,
        exportErrorData: errorFile,
        cleanImportModal: cleanUpModalImport,
      },
      openGenericModal,
      refreshGenericModal,
      get(props, 'modal.GenericModalBool', false),
    );
  };

  const openBatchUpdateImportModal = () => {
    const isModalOpen = get(props, 'modal.GenericModalBool', false);

    if (isModalOpen) {
      return;
    }

    const params = getUpdateIngredientModalConfig(
      IMPORT_MODAL_STEPS.SELECT_FILE,
      {
        handleCloseCleanUp: cleanUpBatchUpdateImport,
        updatedFile: null,
        selectedFile: null,
        handleFileChange: onImportIngredientUpdateFileChange,
      },
      props,
    );

    openGenericModal(params);
  };

  const onImportIngredientUpdateFileChange = (file) => {
    refreshGenericModal(
      getUpdateIngredientModalConfig(IMPORT_MODAL_STEPS.VALIDATE_FILE, {
        updatedFile: file,
        handleCloseCleanUp: cleanUpBatchUpdateImport,
        handleFileChange: onImportIngredientUpdateFileChange,
        handleFileValidation: handleIngredientUpdateFileValidation,
      }),
    );
  };

  const handleIngredientUpdateFileValidation = async (file) => {
    pageLoading();
    try {
      const fileIngredientsData = await formatXlsFileToJson(file);

      const formattedFileIngredientsData = formatEntityBatchUpdateFile(fileIngredientsData);

      const { unValidatedIngredients, unValidatedBufferStocks, unValidatedNonSalesConsumptions } =
        await ingredientService.validateBatchUpdateImportTemplate(formattedFileIngredientsData);

      if (
        !isEmpty(unValidatedIngredients) ||
        !isEmpty(unValidatedBufferStocks) ||
        !isEmpty(unValidatedNonSalesConsumptions)
      ) {
        handlePartiallyUpdated(file, {
          originalFileData: formattedFileIngredientsData,
          unValidatedIngredients,
          unValidatedBufferStocks,
          unValidatedNonSalesConsumptions,
        });

        return;
      }

      refreshGenericModal(
        getUpdateIngredientModalConfig(IMPORT_MODAL_STEPS.VALIDATED_FILE, {
          updatedFile: file,
          storeName: storeNamePlural,
          handleCloseCleanUp: cleanUpBatchUpdateImport,
          handleFileImport: () => handleIngredientUpdateFileImport(formattedFileIngredientsData),
        }),
      );
    } catch (error) {
      handleIngredientUpdateErrorFile(file);
    } finally {
      pageLoaded();
    }
  };

  const cleanUpBatchUpdateImport = () => {
    setSelectedFile(null);
    setErrorFile(null);
    closeGenericModal();
  };

  const handleIngredientUpdateFileImport = async (originalFileData) => {
    pageLoading();

    try {
      const importResult = await ingredientService.batchUpdateImport(originalFileData);

      if (importResult?.hasUpdatedBufferStocks) {
        mixpanelUtils.sendMetric(ENUM_EVENTS.UPDATE_BUFFER_STOCK, {
          source: ENUM_EVENT_SOURCES.BATCH_MODIFICATION_FILE,
        });
      }

      cleanUpBatchUpdateImport();

      await getIngredients();

      showSuccessMessage(i18next.t('ADMIN.INGREDIENTS.IMPORT_FILE_BATCH_UPDATE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.IMPORT_FILE_BATCH_UPDATE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  // This function will be deleted later, it's for test purpose only
  const handlePartiallyUpdated = (
    file,
    {
      originalFileData,
      unValidatedIngredients,
      unValidatedBufferStocks,
      unValidatedNonSalesConsumptions,
    },
  ) => {
    refreshGenericModal(
      getUpdateIngredientModalConfig(IMPORT_MODAL_STEPS.PARTIALLY_VALIDATED_FILE, {
        updatedFile: file,
        handleFileChange: onImportIngredientUpdateFileChange,
        handleCloseCleanUp: cleanUpBatchUpdateImport,
        handleFileImport: () => handleIngredientUpdateFileImport(originalFileData),
        unValidatedLines: [
          ...unValidatedIngredients,
          ...unValidatedBufferStocks,
          ...unValidatedNonSalesConsumptions,
        ],
        errorFile: file,
        exportErrors: () =>
          getImportErrorFileConfig(
            unValidatedIngredients,
            unValidatedBufferStocks,
            unValidatedNonSalesConsumptions,
            prevHorizon.future,
            storeNameSingular,
          ),
      }),
    );
  };

  const handleIngredientUpdateErrorFile = (file) => {
    refreshGenericModal(
      getUpdateIngredientModalConfig(IMPORT_MODAL_STEPS.ERROR_FILE, {
        updatedFile: file,
        handleFileChange: onImportIngredientUpdateFileChange,
        handleCloseCleanUp: cleanUpBatchUpdateImport,
      }),
    );
  };

  const updateListActions = () => {
    const updatedActions = getActions({
      handleExport: handleExport,
      selectedItems: selectedItems,
      goToIngredientDetailsPage: goToIngredientDetailsPage,
      renderCreateButton: renderCreateButton,
      history: history,
      openGenericModal: openGenericModal,
      deleteIngredientByIdsWithLinkedData: deleteIngredientByIdsWithLinkedData,
      updateIsStrategicOfIngredientsByBatch: updateIsStrategicOfIngredientsByBatch,
      handleIngredientsDeactivation: handleIngredientsDeactivation,
      filteringOnActive: activeFilterValue,
      user: user,
      handleIngredientActivation: handleIngredientActivation,
      getEntityBatchCreationTemplate: getEntityBatchCreationTemplate,
      getIngredientsBatchUpdateTemplate: getIngredientsBatchUpdateTemplate,
      openImportEntitiesModal: openImportEntitiesModal,
      authorizedActions: authorizedActions,
      openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
      openBatchUpdateImportModal,
    });

    setActions(updatedActions);
  };

  const updateRowActionsList = () => {
    const updatedRowActions = getRowActions({
      ingredients: ingredients,
      goToIngredientDetailsPage: goToIngredientDetailsPage,
      openGenericModal: openGenericModal,
      deleteIngredientByIdsWithLinkedData: deleteIngredientByIdsWithLinkedData,
      updateIsStrategicOfIngredientsByBatch: updateIsStrategicOfIngredientsByBatch,
      handleIngredientsDeactivation: handleIngredientsDeactivation,
      filteringOnActive: activeFilterValue,
      user: user,
      handleIngredientActivation: handleIngredientActivation,
      openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
    });

    setRowActions(updatedRowActions);
  };

  const updateEmptyStateActions = () => {
    const updatedEmptyStateActions = getEmptyStateActions({
      getEntityBatchCreationTemplate,
      openImportEntitiesModal,
      authorizedActions,
      openCategoriesBatchUpdateModal: categoriesBatchUpdateModal.open,
    });

    setEmptyStateActions(updatedEmptyStateActions);
  };

  const renderCreateButton = () => (
    <Button
      buttonCustomStyle={{ fontWeight: 400 }}
      color={'blue'}
      fontSize={14}
      formatText={false}
      handleClick={() => history.push('/admin/products/ingredients/create')}
      icon={'/images/inpulse/add-white-small.svg'}
      iconCustomStyle={{ width: '14px', height: '14px' }}
      label={i18next.t('GENERAL.CREATE')}
    />
  );

  const triggerExport = async (ingredients) => {
    openModalExportInfo({
      component: IngredientExportModal,
      title: i18next.t('ADMIN.INGREDIENTS.EXPORT'),
      ingredients: ingredients || [],
      customParams: ingredients && ingredients.length > 0 ? null : customParams,
      selectedStatus: ingredients && ingredients.length > 0 ? null : selectedStatus,
      selectedStrategicFilters:
        ingredients && ingredients.length > 0 ? null : selectedStrategicFilters,
    });
  };

  const renderEmptyState = () => (
    <GeneralEmptyStateListView
      headerActions={emptyStateActions}
      icon={'/images/inpulse/ingredients.svg'}
      renderAction={() => (
        <Button
          color="inpulse-default"
          handleClick={() => history.push('/admin/products/ingredients/create')}
          icon={'/images/inpulse/add-white-small.svg'}
          label={i18next.t('ADMIN.INGREDIENTS.CREATE')}
        />
      )}
      subtitle={i18next.t('ADMIN.INGREDIENTS.EMPTY_STATE_SUBTITLE')}
      title={i18next.t('ADMIN.INGREDIENTS.EMPTY_STATE_TITLE')}
    />
  );

  const categoriesBatchUpdateModal = useCategoriesBatchUpdateModal({
    onRefresh: getIngredients,
    loadProps: {
      clientId,
      type: CATEGORY_TYPES_OBJECT.INGREDIENT,
      propertyType: 'categories',
    },
    itemListSorterParams: {
      disableMergeItems: true,
      disableCreateItem: false,
      entityName: i18next.t('FEATURE.ADMIN.INGREDIENTS'),
      createItemLabel: i18next.t('ADMIN.GENERAL.MANAGE_CATEGORIES_CREATE_LABEL'),
    },
  });

  if (!paginationProps) {
    return <></>;
  }

  return (
    <>
      <NavigationBreadCrumb featurePath={path} />
      {emptyState ? (
        renderEmptyState()
      ) : (
        <ListViewContainer>
          <ListView
            {...paginationProps}
            actionOnClick={(ingredient) => goToIngredientDetailsPage(ingredient.id)}
            actions={actions}
            columns={columns}
            data={ingredients}
            defaultSearchInput={search}
            isLoading={isLoading}
            languageCode={userLanguageCode}
            markerConfiguration={{
              isHidden: ({ isStrategic }) => !isStrategic,
              backgroundColor: getTheme().colors.greys.darkest,
              icon: {
                src: '/images/inpulse/icon-label-important-white.svg',
              },
            }}
            minActionsInActionsDropdown={1}
            padding={STANDARD_LISTVIEW_PADDING}
            placeholderShape={i18next.t('GENERAL.SEARCH')}
            renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
            renderFilterButton={() => (
              <DeepsightFiltersButton
                advancedFilters={advancedFilters}
                applyFilters={applyFilters}
                columnsFilterList={columnsFilterList}
                customMultipleDropDowns={[
                  {
                    id: ENUM_CUSTOM_FILTERS.IS_STRATEGIC,
                    icon: '/images/icon-dropdown-grey.svg',
                    list: STRATEGIC_INGREDIENT_DROPDOWN_ITEMS(),
                    defaultSelectedItems: STRATEGIC_INGREDIENT_DROPDOWN_ITEMS(),
                    selectedItems: selectedStrategicFilters,
                    setSelectedItems: setSelectedStrategicFilters,
                  },
                ]}
                customSingleDropDowns={[
                  {
                    id: 'active-inactive-supplier-products',
                    isDisabled: isUserManager(user) && !userHasRoleInpulse(user),
                    disabledIcon: '/images/inpulse/power-ip-black.svg',
                    itemSelectedIcon: '/images/inpulse/power-ip-black.svg',
                    list: CHOICES_DROPDOWN_ACTIVE(),
                    defaultSelectedItem: CHOICES_DROPDOWN_ACTIVE()[0],
                    selectedItem: selectedStatus,
                    setSelectedItem: setSelectedStatus,
                  },
                ]}
                filters={filters}
                isLoading={isLoading}
                readOnly={isLoading}
                setAdvancedFilters={setAdvancedFilters}
                setApplyFilters={setApplyFilters}
                setFilters={setFilters}
                textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
              />
            )}
            rowActions={rowActions}
            setSelectedItems={setSelectedItems}
          />
        </ListViewContainer>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  modal: state.modalReducer,
  user: state.baseReducer.user,
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/admin/products/ingredients',
  ),
  prevHorizon: getPrevHorizon(state.baseReducer.userRights),
});

const mapDispatchToProps = (dispatch) => ({
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params, customPropertyPathsToUpdate) => {
    dispatch(refreshGenericModal(params, customPropertyPathsToUpdate));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Ingredients);
